import React from 'react';
import {
  Box,
  Paper,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const HourlyCallChart = ({ data, loading }) => {
  const theme = useTheme();

  // Sample data for development - will be replaced with real API data
  // talkTime is in minutes
  const sampleData = [
    { hour: '9 AM', contacted: 45, connected: 32, talkTime: 125 },
    { hour: '10 AM', contacted: 52, connected: 38, talkTime: 142 },
    { hour: '11 AM', contacted: 48, connected: 35, talkTime: 138 },
    { hour: '12 PM', contacted: 38, connected: 28, talkTime: 98 },
    { hour: '1 PM', contacted: 30, connected: 22, talkTime: 75 },
    { hour: '2 PM', contacted: 42, connected: 31, talkTime: 115 },
    { hour: '3 PM', contacted: 55, connected: 40, talkTime: 165 },
    { hour: '4 PM', contacted: 50, connected: 36, talkTime: 148 },
    { hour: '5 PM', contacted: 47, connected: 34, talkTime: 132 },
    { hour: '6 PM', contacted: 35, connected: 25, talkTime: 88 },
  ];

  const chartData = data || sampleData;

  // Format talk time from minutes to readable format
  const formatTalkTime = (minutes) => {
    if (!minutes) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            p: 1.5,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 1,
            }}
          >
            {label}
          </Typography>
          {payload.map((entry, index) => {
            let displayValue = entry.value;
            if (entry.dataKey === 'talkTime') {
              displayValue = formatTalkTime(entry.value);
            }
            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: entry.dataKey === 'talkTime' ? 2 : 12,
                    height: entry.dataKey === 'talkTime' ? 12 : 12,
                    borderRadius: entry.dataKey === 'talkTime' ? 0 : '50%',
                    backgroundColor: entry.color,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                  }}
                >
                  {entry.name}:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '0.875rem',
                  }}
                >
                  {displayValue}
                </Typography>
              </Box>
            );
          })}
        </Box>
      );
    }
    return null;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        background: 'background.paper',
        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.05)}`,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: 'text.primary',
          mb: 2,
          fontSize: '1.1rem',
        }}
      >
        Hourly Call Performance
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 3,
          fontSize: '0.875rem',
        }}
      >
        Calls contacted vs connected and talk time by hour
      </Typography>

      <ResponsiveContainer width="100%" height={350}>
        <ComposedChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
          <XAxis
            dataKey="hour"
            tick={{
              fontSize: 12,
              fill: theme.palette.text.secondary,
            }}
          />
          <YAxis
            yAxisId="left"
            tick={{
              fontSize: 12,
              fill: theme.palette.text.secondary,
            }}
            label={{
              value: 'Number of Calls',
              angle: -90,
              position: 'insideLeft',
              style: {
                fontSize: 12,
                fill: theme.palette.text.secondary,
              },
            }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{
              fontSize: 12,
              fill: theme.palette.text.secondary,
            }}
            label={{
              value: 'Talk Time (minutes)',
              angle: 90,
              position: 'insideRight',
              style: {
                fontSize: 12,
                fill: theme.palette.text.secondary,
              },
            }}
          />
          <RechartsTooltip content={<CustomTooltip />} cursor={{ fill: alpha(theme.palette.primary.main, 0.05) }} />
          <Legend
            wrapperStyle={{
              paddingTop: '20px',
            }}
            iconType="circle"
          />
          <Bar
            yAxisId="left"
            dataKey="contacted"
            name="Contacted"
            fill={theme.palette.info.main}
            radius={[4, 4, 0, 0]}
            maxBarSize={50}
          />
          <Bar
            yAxisId="left"
            dataKey="connected"
            name="Connected"
            fill={theme.palette.success.main}
            radius={[4, 4, 0, 0]}
            maxBarSize={50}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="talkTime"
            name="Talk Time"
            stroke={theme.palette.warning.main}
            strokeWidth={3}
            dot={{ fill: theme.palette.warning.main, r: 4 }}
            activeDot={{ r: 6 }}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Summary Stats */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-around',
          mt: 3,
          pt: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: theme.palette.info.main,
              mb: 0.5,
            }}
          >
            {chartData.reduce((sum, item) => sum + (item.contacted || 0), 0)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
            }}
          >
            Total Contacted
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: theme.palette.success.main,
              mb: 0.5,
            }}
          >
            {chartData.reduce((sum, item) => sum + (item.connected || 0), 0)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
            }}
          >
            Total Connected
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: theme.palette.warning.main,
              mb: 0.5,
            }}
          >
            {formatTalkTime(chartData.reduce((sum, item) => sum + (item.talkTime || 0), 0))}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
            }}
          >
            Total Talk Time
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: theme.palette.primary.main,
              mb: 0.5,
            }}
          >
            {chartData.length > 0 && chartData.reduce((sum, item) => sum + (item.contacted || 0), 0) > 0
              ? Math.round(
                  (chartData.reduce((sum, item) => sum + (item.connected || 0), 0) /
                    chartData.reduce((sum, item) => sum + (item.contacted || 0), 0)) *
                    100
                )
              : 0}
            %
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
            }}
          >
            Connection Rate
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default HourlyCallChart;
